import { useId, useState } from 'react';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { AccionConfirmada } from '../componentes/AccionConfirmada.js';
import { Icono } from '../componentes/Iconos.js';
import { GaleriaFotos } from '../componentes/GaleriaFotos.js';
import { fechaLarga } from '../componentes.js';
import { responsables } from './datos.js';
import { idDe, type Accion_ } from './estado.js';
import {
  CANALES,
  CONFIANZAS,
  ETAPAS,
  FASES,
  INFLUENCIAS,
  PRIORIDADES,
  ROLES_CONTACTO,
  TIPOS_ACCION,
  etapaActiva,
  frescuraDe,
  tieneContactoDirecto,
  type Contacto,
  type Obra,
} from './modelo.js';
import { Dinero, Frescura, PastillaEtapa, Progreso, ProximaAccion, Puntuacion } from './piezas.jsx';

/**
 * La ficha completa de una oportunidad.
 *
 * Seis pestanas porque son seis preguntas distintas —como va, que ha pasado,
 * con quien hablo, que hay que hacer, como va la obra y quien toco que— y
 * apilarlas en una sola pagina obligaria a desplazarse para responder
 * cualquiera de ellas.
 *
 * Todo lo que cambia aqui deja un apunte en el historial. No hay forma de mover
 * una oportunidad en silencio.
 */

/*
  LAS FOTOS VAN LAS SEGUNDAS, no al final. Una obra se reconoce por su foto
  mucho antes que por su direccion, y como va se ve mirando la ultima al lado de
  la de hace un mes: es de lo primero que se abre, no un anexo.
*/
const PESTANAS = [
  'resumen',
  'fotos',
  'seguimiento',
  'contactos',
  'tareas',
  'obra',
  'historial',
] as const;
type Pestana = (typeof PESTANAS)[number];

interface Props {
  obra: Obra;
  idioma: Idioma;
  hoy: string;
  quien: string;
  puedeEditar: boolean;
  onVolver: () => void;
  onEditar: () => void;
  onConvertir: () => void;
  despachar: (a: Accion_) => void;
  /** Recien dada de alta con fotos, se abre en ellas: es lo que se acaba de hacer. */
  pestanaInicial?: 'fotos';
}

export function Detalle({
  obra,
  idioma,
  hoy,
  quien,
  puedeEditar,
  onVolver,
  onEditar,
  onConvertir,
  despachar,
  pestanaInicial,
}: Props) {
  const [pestana, setPestana] = useState<Pestana>(pestanaInicial ?? 'resumen');
  const promotor = obra.contactos.find((c) => c.rol === 'PROMOTOR')?.nombre ?? null;

  return (
    <div className="obra-detalle">
      <div className="cabecera-seccion">
        <div>
          <span className="rotulo">
            {obra.codigo} · {t(idioma, `obras.tipo.${obra.tipoProyecto}`)}
          </span>
          <h1>{obra.nombre}</h1>
          <p>
            <Icono nombre="pin" /> {obra.direccion} · {obra.zona} · {obra.municipio}
          </p>
        </div>
        <div className="acciones">
          <button type="button" className="secundario" onClick={onVolver}>
            <Icono nombre="volver" /> {t(idioma, 'obras.volverLista')}
          </button>
          {puedeEditar && (
            <button type="button" className="secundario" onClick={onEditar}>
              <Icono nombre="lapiz" /> {t(idioma, 'obras.editar')}
            </button>
          )}
        </div>
      </div>

      <div className="obra-cabecera-datos">
        <PastillaEtapa etapa={obra.etapa} idioma={idioma} />
        <Puntuacion obra={obra} idioma={idioma} hoy={hoy} />
        <Frescura obra={obra} idioma={idioma} hoy={hoy} />
        <Progreso obra={obra} idioma={idioma} />
        <span className="suave">
          {t(idioma, 'obras.responsable')}: {obra.responsable ?? t(idioma, 'obras.sinResponsable')}
        </span>
        {promotor && (
          <span className="suave">
            {t(idioma, 'obras.promotor')}: <b>{promotor}</b>
          </span>
        )}
      </div>

      <div className="pestanas" role="tablist">
        {PESTANAS.map((p) => (
          <button
            key={p}
            type="button"
            role="tab"
            aria-selected={pestana === p}
            onClick={() => setPestana(p)}
          >
            {t(idioma, `obras.tab.${p}`)}
          </button>
        ))}
      </div>

      {pestana === 'resumen' && (
        <Resumen
          obra={obra}
          idioma={idioma}
          hoy={hoy}
          quien={quien}
          puedeEditar={puedeEditar}
          onConvertir={onConvertir}
          despachar={despachar}
        />
      )}
      {pestana === 'fotos' && (
        <>
          <GaleriaFotos
            idioma={idioma}
            entidad="OBRA"
            entidadId={obra.id}
            puedeGestionar={puedeEditar}
            titulo={t(idioma, 'fotos.obra')}
          />
          <p className="suave">{t(idioma, 'fotos.obraNota')}</p>
        </>
      )}
      {pestana === 'seguimiento' && (
        <Seguimiento obra={obra} idioma={idioma} quien={quien} puedeEditar={puedeEditar} despachar={despachar} />
      )}
      {pestana === 'contactos' && (
        <Contactos obra={obra} idioma={idioma} quien={quien} puedeEditar={puedeEditar} despachar={despachar} />
      )}
      {pestana === 'tareas' && (
        <Tareas obra={obra} idioma={idioma} hoy={hoy} quien={quien} puedeEditar={puedeEditar} despachar={despachar} />
      )}
      {pestana === 'obra' && (
        <InfoObra obra={obra} idioma={idioma} quien={quien} puedeEditar={puedeEditar} despachar={despachar} />
      )}
      {pestana === 'historial' && <Historial obra={obra} idioma={idioma} />}
    </div>
  );
}

// --- Resumen -----------------------------------------------------------------

function Resumen({
  obra,
  idioma,
  hoy,
  quien,
  puedeEditar,
  onConvertir,
  despachar,
}: {
  obra: Obra;
  idioma: Idioma;
  hoy: string;
  quien: string;
  puedeEditar: boolean;
  onConvertir: () => void;
  despachar: (a: Accion_) => void;
}) {
  const riesgos: string[] = [];
  if (obra.situacion.propietarioConocido !== true) riesgos.push('obras.riesgoSinPropietario');
  if (!tieneContactoDirecto(obra)) riesgos.push('obras.riesgoSinContacto');
  if (obra.finalizacionEstimada === null) riesgos.push('obras.riesgoSinFecha');
  if (obra.valorMercadoCents === null) riesgos.push('obras.riesgoSinValor');
  const f = frescuraDe(obra, hoy);
  if (f === 'DESACTUALIZADA' || f === 'SIN_VERIFICAR') riesgos.push('obras.riesgoVieja');

  const promotor = obra.contactos.filter((c) =>
    ['PROPIETARIO', 'PROMOTOR', 'COMERCIALIZADORA'].includes(c.rol),
  );

  return (
    <>
      <div className="rejilla dos">
        <section className="tarjeta">
          <h2>{t(idioma, 'obras.situacionFisica')}</h2>
          <dl className="obra-datos">
            <dt>{t(idioma, 'obras.fase')}</dt>
            <dd>{t(idioma, `obras.fase.${obra.fase}`)}</dd>
            <dt>{t(idioma, 'obras.progreso')}</dt>
            <dd>
              <Progreso obra={obra} idioma={idioma} />
            </dd>
            <dt>{t(idioma, 'obras.finEstimada')}</dt>
            <dd>{fechaLarga(obra.finalizacionEstimada, idioma)}</dd>
            <dt>{t(idioma, 'obras.confianza')}</dt>
            <dd>{t(idioma, `obras.confianza.${obra.confianza}`)}</dd>
            <dt>{t(idioma, 'obras.ultimaComprobacion')}</dt>
            <dd>
              {fechaLarga(obra.ultimaComprobacion, idioma)}
              {obra.comprobadaPor && <small className="suave"> · {obra.comprobadaPor}</small>}
            </dd>
          </dl>
          {obra.comentarioObra && <p className="suave">{obra.comentarioObra}</p>}
        </section>

        <section className="tarjeta">
          <h2>{t(idioma, 'obras.situacionComercial')}</h2>
          <dl className="obra-datos">
            <dt>{t(idioma, 'obras.etapaComercial')}</dt>
            <dd>
              <PastillaEtapa etapa={obra.etapa} idioma={idioma} />
            </dd>
            <dt>{t(idioma, 'obras.responsable')}</dt>
            <dd>{obra.responsable ?? <span className="suave">{t(idioma, 'obras.sinResponsable')}</span>}</dd>
            <dt>{t(idioma, 'obras.valorMercado')}</dt>
            <dd>
              <Dinero cents={obra.valorMercadoCents} idioma={idioma} />
            </dd>
            <dt>{t(idioma, 'obras.potencial')}</dt>
            <dd>
              <Dinero cents={obra.potencialCents} idioma={idioma} />
            </dd>
            <dt>{t(idioma, 'obras.promotorPropietario')}</dt>
            <dd>
              {promotor.length === 0 ? (
                <span className="suave">{t(idioma, 'obras.sinDato')}</span>
              ) : (
                promotor.map((c) => (
                  <span key={c.id} className="pastilla neutra">
                    {c.nombre} · {t(idioma, `obras.rol.${c.rol}`)}
                  </span>
                ))
              )}
            </dd>
          </dl>
          {obra.motivoPerdida && (
            <p className="error" role="status">
              {t(idioma, 'obras.motivoPerdida')}: {obra.motivoPerdida}
            </p>
          )}
        </section>
      </div>

      <section className="tarjeta">
        <div className="tarjeta-cabeza">
          <h2>{t(idioma, 'obras.proximaAccion')}</h2>
          <ProximaAccion obra={obra} idioma={idioma} hoy={hoy} detallada />
        </div>
        {etapaActiva(obra.etapa) && obra.acciones.filter((a) => !a.hecha).length === 0 && (
          <p className="aviso-demo" role="status">
            {t(idioma, 'obras.sinProximaAviso')}
          </p>
        )}
      </section>

      <section className="tarjeta">
        <h2>{t(idioma, 'obras.riesgos')}</h2>
        {riesgos.length === 0 ? (
          <p className="suave">{t(idioma, 'obras.sinRiesgos')}</p>
        ) : (
          <ul className="obra-riesgos">
            {riesgos.map((r) => (
              <li key={r}>
                <Icono nombre="aviso" /> {t(idioma, r)}
              </li>
            ))}
          </ul>
        )}
      </section>

      {puedeEditar && (
        <CambioEtapa obra={obra} idioma={idioma} quien={quien} onConvertir={onConvertir} despachar={despachar} />
      )}
    </>
  );
}

// --- Cambio de etapa ---------------------------------------------------------

function CambioEtapa({
  obra,
  idioma,
  quien,
  onConvertir,
  despachar,
}: {
  obra: Obra;
  idioma: Idioma;
  quien: string;
  onConvertir: () => void;
  despachar: (a: Accion_) => void;
}) {
  const [etapa, setEtapa] = useState(obra.etapa);
  const [comentario, setComentario] = useState('');
  const [error, setError] = useState<string | null>(null);
  const ids = { etapa: useId(), comentario: useId() };

  const aplicar = () => {
    // «Perdida» exige motivo: sin el, dentro de seis meses nadie sabra por que
    // se dejo de trabajar esta obra y se volvera a empezar desde cero.
    if (etapa === 'PERDIDA' && comentario.trim().length < 3) {
      setError(t(idioma, 'obras.pidePerdida'));
      return;
    }
    setError(null);
    despachar({ tipo: 'etapa', obraId: obra.id, etapa, quien, comentario });
    setComentario('');
  };

  return (
    <section className="tarjeta">
      <h2>{t(idioma, 'obras.cambiarEtapa')}</h2>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div className="rejilla dos">
        <div className="campo">
          <label htmlFor={ids.etapa}>{t(idioma, 'obras.etapaComercial')}</label>
          <select
            id={ids.etapa}
            value={etapa}
            onChange={(e) => {
              // El aviso era sobre la etapa anterior: al cambiarla deja de ser
              // cierto, y un error que no corresponde a lo que hay en pantalla
              // ensena a la gente a ignorar los errores.
              setError(null);
              setEtapa(e.target.value as Obra['etapa']);
            }}
          >
            {ETAPAS.map((et) => (
              <option key={et} value={et}>
                {t(idioma, `obras.etapa.${et}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor={ids.comentario}>
            {t(idioma, etapa === 'PERDIDA' ? 'obras.motivoPerdida' : 'obras.comentarioEtapa')}
          </label>
          <input
            id={ids.comentario}
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
          />
        </div>
      </div>

      <div className="acciones">
        {/* Ganar una oportunidad no se hace de un clic: cierra la venta y abre
            la conversion en cliente y propiedad. */}
        {etapa === 'GANADA' && obra.etapa !== 'GANADA' ? (
          <AccionConfirmada
            idioma={idioma}
            etiqueta={t(idioma, 'obras.confirmarGanada')}
            consecuencias={t(idioma, 'obras.consecuenciaGanada')}
            onConfirmar={() => {
              despachar({ tipo: 'etapa', obraId: obra.id, etapa: 'GANADA', quien, comentario });
              onConvertir();
            }}
          />
        ) : (
          <button type="button" onClick={aplicar} disabled={etapa === obra.etapa}>
            {t(idioma, 'obras.cambiarEtapa')}
          </button>
        )}

        {obra.etapa === 'GANADA' && !obra.convertidaEn && (
          <button type="button" className="secundario" onClick={onConvertir}>
            {t(idioma, 'obras.convertir')}
          </button>
        )}
        {obra.convertidaEn && (
          <span className="pastilla VERDE">
            {t(idioma, 'obras.convertida', {
              cliente: obra.convertidaEn.clienteId,
              propiedad: obra.convertidaEn.propiedadId,
            })}
          </span>
        )}
      </div>
    </section>
  );
}

// --- Seguimiento -------------------------------------------------------------

const CLASES_SEGUIMIENTO = ['NOTA', 'LLAMADA', 'EMAIL', 'WHATSAPP', 'VISITA', 'REUNION'] as const;

function Seguimiento({
  obra,
  idioma,
  quien,
  puedeEditar,
  despachar,
}: {
  obra: Obra;
  idioma: Idioma;
  quien: string;
  puedeEditar: boolean;
  despachar: (a: Accion_) => void;
}) {
  const [clase, setClase] = useState<(typeof CLASES_SEGUIMIENTO)[number]>('NOTA');
  const [texto, setTexto] = useState('');
  const ids = { clase: useId(), texto: useId() };

  return (
    <section className="tarjeta">
      <h2>{t(idioma, 'obras.tab.seguimiento')}</h2>
      <p className="suave">{t(idioma, 'obras.seguimientoNota')}</p>

      {puedeEditar && (
        <div className="fila-flex">
          <div className="campo">
            <label htmlFor={ids.clase}>{t(idioma, 'obras.registrar')}</label>
            <select
              id={ids.clase}
              value={clase}
              onChange={(e) => setClase(e.target.value as (typeof CLASES_SEGUIMIENTO)[number])}
            >
              {CLASES_SEGUIMIENTO.map((c) => (
                <option key={c} value={c}>
                  {t(idioma, `obras.apunte.${c}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="campo crecer">
            <label htmlFor={ids.texto}>{t(idioma, 'obras.queHaPasado')}</label>
            <input id={ids.texto} value={texto} onChange={(e) => setTexto(e.target.value)} />
          </div>
          <button
            type="button"
            disabled={texto.trim().length < 2}
            onClick={() => {
              despachar({ tipo: 'seguimiento', obraId: obra.id, clase, texto, quien });
              setTexto('');
            }}
          >
            {t(idioma, 'obras.registrar')}
          </button>
        </div>
      )}

      <Cronologia obra={obra} idioma={idioma} />
    </section>
  );
}

// --- Contactos ---------------------------------------------------------------

function Contactos({
  obra,
  idioma,
  quien,
  puedeEditar,
  despachar,
}: {
  obra: Obra;
  idioma: Idioma;
  quien: string;
  puedeEditar: boolean;
  despachar: (a: Accion_) => void;
}) {
  const [nuevo, setNuevo] = useState<Contacto | null>(null);
  const ids = { nombre: useId(), rol: useId(), tel: useId(), email: useId(), web: useId(), canal: useId(), infl: useId(), notas: useId() };

  const vacio = (): Contacto => ({
    id: idDe('cto'),
    nombre: '',
    rol: 'PROPIETARIO',
    telefono: null,
    email: null,
    web: null,
    idioma: null,
    canal: null,
    influencia: null,
    notas: null,
    sinContacto: false,
  });

  return (
    <section className="tarjeta">
      <div className="tarjeta-cabeza">
        <h2>{t(idioma, 'obras.tab.contactos')}</h2>
        {puedeEditar && !nuevo && (
          <button type="button" className="secundario" onClick={() => setNuevo(vacio())}>
            {t(idioma, 'obras.anadirContacto')}
          </button>
        )}
      </div>

      {obra.contactos.length === 0 && !nuevo && (
        <p className="suave">{t(idioma, 'obras.sinContactos')}</p>
      )}

      <ul className="lista">
        {obra.contactos.map((c) => (
          <li key={c.id} className="fila">
            <span className="crecer">
              <b>
                {c.nombre}
                {c.sinContacto && (
                  <span className="pastilla ROJO"> {t(idioma, 'obras.sinContactarAviso')}</span>
                )}
              </b>
              <small>
                {t(idioma, `obras.rol.${c.rol}`)}
                {c.influencia && ` · ${t(idioma, `obras.influencia.${c.influencia}`)}`}
                {c.canal && ` · ${t(idioma, `obras.canal.${c.canal}`)}`}
                {c.idioma && ` · ${c.idioma.toUpperCase()}`}
              </small>
              <small className="suave">
                {[c.telefono, c.email, c.web].filter(Boolean).join(' · ')}
              </small>
              {c.notas && <small className="suave">{c.notas}</small>}
            </span>
            {puedeEditar && (
              <button
                type="button"
                className="enlace"
                onClick={() => despachar({ tipo: 'contactoBaja', obraId: obra.id, contactoId: c.id, quien })}
              >
                {t(idioma, 'obras.quitarContacto')}
              </button>
            )}
          </li>
        ))}
      </ul>

      {nuevo && (
        <div className="obra-contacto-alta">
          <div className="rejilla dos">
            <div className="campo">
              <label htmlFor={ids.nombre}>{t(idioma, 'obras.nombrePersonaEmpresa')}</label>
              <input
                id={ids.nombre}
                value={nuevo.nombre}
                onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })}
              />
            </div>
            <div className="campo">
              <label htmlFor={ids.rol}>{t(idioma, 'obras.rolEnObra')}</label>
              <select
                id={ids.rol}
                value={nuevo.rol}
                onChange={(e) => setNuevo({ ...nuevo, rol: e.target.value as Contacto['rol'] })}
              >
                {ROLES_CONTACTO.map((r) => (
                  <option key={r} value={r}>
                    {t(idioma, `obras.rol.${r}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="campo">
              <label htmlFor={ids.tel}>{t(idioma, 'obras.telefono')}</label>
              <input
                id={ids.tel}
                value={nuevo.telefono ?? ''}
                onChange={(e) => setNuevo({ ...nuevo, telefono: e.target.value || null })}
              />
            </div>
            <div className="campo">
              <label htmlFor={ids.email}>{t(idioma, 'obras.email')}</label>
              <input
                id={ids.email}
                type="email"
                value={nuevo.email ?? ''}
                onChange={(e) => setNuevo({ ...nuevo, email: e.target.value || null })}
              />
            </div>
            <div className="campo">
              <label htmlFor={ids.web}>{t(idioma, 'obras.web')}</label>
              <input
                id={ids.web}
                value={nuevo.web ?? ''}
                onChange={(e) => setNuevo({ ...nuevo, web: e.target.value || null })}
              />
            </div>
            <div className="campo">
              <label htmlFor={ids.canal}>{t(idioma, 'obras.canalPreferido')}</label>
              <select
                id={ids.canal}
                value={nuevo.canal ?? ''}
                onChange={(e) => setNuevo({ ...nuevo, canal: (e.target.value || null) as Contacto['canal'] })}
              >
                <option value="">{t(idioma, 'obras.sinDato')}</option>
                {CANALES.map((c) => (
                  <option key={c} value={c}>
                    {t(idioma, `obras.canal.${c}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="campo">
              <label htmlFor={ids.infl}>{t(idioma, 'obras.nivelDecision')}</label>
              <select
                id={ids.infl}
                value={nuevo.influencia ?? ''}
                onChange={(e) =>
                  setNuevo({ ...nuevo, influencia: (e.target.value || null) as Contacto['influencia'] })
                }
              >
                <option value="">{t(idioma, 'obras.sinDato')}</option>
                {INFLUENCIAS.map((i) => (
                  <option key={i} value={i}>
                    {t(idioma, `obras.influencia.${i}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="campo">
              <label htmlFor={ids.notas}>{t(idioma, 'obras.notas')}</label>
              <input
                id={ids.notas}
                value={nuevo.notas ?? ''}
                onChange={(e) => setNuevo({ ...nuevo, notas: e.target.value || null })}
              />
            </div>
          </div>

          <label className="fila-flex pequeno">
            <input
              type="checkbox"
              checked={nuevo.sinContacto}
              onChange={(e) => setNuevo({ ...nuevo, sinContacto: e.target.checked })}
            />
            {t(idioma, 'obras.sinContactar')}
          </label>

          <div className="acciones">
            <button type="button" className="secundario" onClick={() => setNuevo(null)}>
              {t(idioma, 'obras.cancelar')}
            </button>
            <button
              type="button"
              disabled={nuevo.nombre.trim().length < 2}
              onClick={() => {
                despachar({ tipo: 'contactoAlta', obraId: obra.id, contacto: nuevo, quien });
                setNuevo(null);
              }}
            >
              {t(idioma, 'obras.anadirContacto')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// --- Tareas ------------------------------------------------------------------

function Tareas({
  obra,
  idioma,
  hoy,
  quien,
  puedeEditar,
  despachar,
}: {
  obra: Obra;
  idioma: Idioma;
  hoy: string;
  quien: string;
  puedeEditar: boolean;
  despachar: (a: Accion_) => void;
}) {
  const [tipo, setTipo] = useState<Obra['acciones'][number]['tipo']>('LLAMAR');
  const [descripcion, setDescripcion] = useState('');
  // Quien la hace: por defecto quien lleva la obra, y si no la lleva nadie,
  // quien la esta escribiendo. Nunca un nombre que no sea de la casa.
  const quienes = responsables([obra], quien);
  const [responsable, setResponsable] = useState(obra.responsable ?? quien);
  const [cuando, setCuando] = useState(`${hoy.slice(0, 10)}T10:00`);
  const [prioridad, setPrioridad] = useState<Obra['acciones'][number]['prioridad']>('MEDIA');
  const [recordatorio, setRecordatorio] = useState(false);
  const ids = { tipo: useId(), desc: useId(), resp: useId(), cuando: useId(), prio: useId() };

  return (
    <section className="tarjeta">
      <h2>{t(idioma, 'obras.tab.tareas')}</h2>

      <ul className="lista">
        {obra.acciones.length === 0 && <li className="suave">{t(idioma, 'obras.sinProxima')}</li>}
        {obra.acciones.map((a) => (
          <li key={a.id} className={`fila${a.hecha ? ' tarea-hecha' : ''}`}>
            <span className="crecer">
              <b>
                {t(idioma, `obras.accionTipo.${a.tipo}`)} · {a.descripcion}
              </b>
              <small>
                {fechaLarga(a.cuando, idioma)} · {a.responsable} ·{' '}
                {t(idioma, `obras.prioridad.${a.prioridad}`)}
                {a.recordatorio && ` · ${t(idioma, 'obras.recordatorio')}`}
              </small>
            </span>
            {!a.hecha && puedeEditar && (
              <button
                type="button"
                className="enlace"
                onClick={() => despachar({ tipo: 'accionHecha', obraId: obra.id, accionId: a.id, quien })}
              >
                {t(idioma, 'obras.marcarHecha')}
              </button>
            )}
          </li>
        ))}
      </ul>

      {puedeEditar && (
        <>
          <h3 className="rotulo">{t(idioma, 'obras.nuevaAccion')}</h3>
          <div className="rejilla dos">
            <div className="campo">
              <label htmlFor={ids.tipo}>{t(idioma, 'obras.registrar')}</label>
              <select
                id={ids.tipo}
                value={tipo}
                onChange={(e) => setTipo(e.target.value as Obra['acciones'][number]['tipo'])}
              >
                {TIPOS_ACCION.map((ta) => (
                  <option key={ta} value={ta}>
                    {t(idioma, `obras.accionTipo.${ta}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="campo">
              <label htmlFor={ids.desc}>{t(idioma, 'obras.descripcion')}</label>
              <input id={ids.desc} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
            </div>
            <div className="campo">
              <label htmlFor={ids.resp}>{t(idioma, 'obras.quienLaHace')}</label>
              <select id={ids.resp} value={responsable} onChange={(e) => setResponsable(e.target.value)}>
                {quienes.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="campo">
              <label htmlFor={ids.cuando}>{t(idioma, 'obras.cuando')}</label>
              <input
                id={ids.cuando}
                type="datetime-local"
                value={cuando}
                onChange={(e) => setCuando(e.target.value)}
              />
            </div>
            <div className="campo">
              <label htmlFor={ids.prio}>{t(idioma, 'obras.prioridadCampo')}</label>
              <select
                id={ids.prio}
                value={prioridad}
                onChange={(e) => setPrioridad(e.target.value as Obra['acciones'][number]['prioridad'])}
              >
                {PRIORIDADES.map((p) => (
                  <option key={p} value={p}>
                    {t(idioma, `obras.prioridad.${p}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="fila-flex pequeno">
            <input
              type="checkbox"
              checked={recordatorio}
              onChange={(e) => setRecordatorio(e.target.checked)}
            />
            {t(idioma, 'obras.recordatorio')}
          </label>
          <p className="suave pequeno">{t(idioma, 'obras.recordatorioNota')}</p>

          <button
            type="button"
            disabled={descripcion.trim().length < 2}
            onClick={() => {
              despachar({
                tipo: 'accionAlta',
                obraId: obra.id,
                quien,
                accion: {
                  id: idDe('acc'),
                  tipo,
                  descripcion,
                  responsable,
                  cuando,
                  prioridad,
                  recordatorio,
                  hecha: false,
                },
              });
              setDescripcion('');
            }}
          >
            {t(idioma, 'obras.anadirAccion')}
          </button>
        </>
      )}
    </section>
  );
}

// --- Informacion de la obra --------------------------------------------------

function InfoObra({
  obra,
  idioma,
  quien,
  puedeEditar,
  despachar,
}: {
  obra: Obra;
  idioma: Idioma;
  quien: string;
  puedeEditar: boolean;
  despachar: (a: Accion_) => void;
}) {
  const [fase, setFase] = useState(obra.fase);
  const [progreso, setProgreso] = useState(obra.progreso);
  const [confianza, setConfianza] = useState(obra.confianza);
  const [fin, setFin] = useState(obra.finalizacionEstimada ?? '');
  const [comentario, setComentario] = useState('');
  const ids = { fase: useId(), progreso: useId(), confianza: useId(), fin: useId(), com: useId() };

  return (
    <section className="tarjeta">
      <h2>{t(idioma, 'obras.tab.obra')}</h2>
      <p className="suave">{t(idioma, 'obras.zonaNota')}</p>

      <div className="rejilla dos">
        <div className="campo">
          <label htmlFor={ids.fase}>{t(idioma, 'obras.fase')}</label>
          <select
            id={ids.fase}
            value={fase}
            disabled={!puedeEditar}
            onChange={(e) => setFase(e.target.value as Obra['fase'])}
          >
            {FASES.map((f) => (
              <option key={f} value={f}>
                {t(idioma, `obras.fase.${f}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor={ids.progreso}>{t(idioma, 'obras.progreso')}</label>
          <input
            id={ids.progreso}
            type="number"
            min={0}
            max={100}
            value={progreso}
            disabled={!puedeEditar}
            onChange={(e) => setProgreso(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
          />
        </div>
        <div className="campo">
          <label htmlFor={ids.fin}>{t(idioma, 'obras.finEstimada')}</label>
          <input
            id={ids.fin}
            type="date"
            value={fin}
            disabled={!puedeEditar}
            onChange={(e) => setFin(e.target.value)}
          />
        </div>
        <div className="campo">
          <label htmlFor={ids.confianza}>{t(idioma, 'obras.confianza')}</label>
          <select
            id={ids.confianza}
            value={confianza}
            disabled={!puedeEditar}
            onChange={(e) => setConfianza(e.target.value as Obra['confianza'])}
          >
            {CONFIANZAS.map((c) => (
              <option key={c} value={c}>
                {t(idioma, `obras.confianza.${c}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="campo">
        <label htmlFor={ids.com}>{t(idioma, 'obras.comprobarNota')}</label>
        <input
          id={ids.com}
          value={comentario}
          disabled={!puedeEditar}
          onChange={(e) => setComentario(e.target.value)}
        />
      </div>

      {puedeEditar && (
        <button
          type="button"
          onClick={() =>
            despachar({
              tipo: 'progreso',
              obraId: obra.id,
              fase,
              progreso,
              confianza,
              finalizacion: fin || null,
              quien,
              comentario,
            })
          }
        >
          {t(idioma, 'obras.comprobar')}
        </button>
      )}
    </section>
  );
}

// --- Historial ---------------------------------------------------------------

function Cronologia({ obra, idioma }: { obra: Obra; idioma: Idioma }) {
  return (
    <ol className="obra-cronologia">
      {obra.historial.map((a) => (
        <li key={a.id}>
          <span className={`pastilla neutra apunte-${a.tipo.toLowerCase()}`}>
            {t(idioma, `obras.apunte.${a.tipo}`)}
          </span>
          <span className="crecer">
            {a.texto && <b>{a.texto}</b>}
            <small className="suave">
              {fechaLarga(a.cuando, idioma)} · {a.quien}
            </small>
          </span>
        </li>
      ))}
    </ol>
  );
}

function Historial({ obra, idioma }: { obra: Obra; idioma: Idioma }) {
  return (
    <section className="tarjeta">
      <h2>{t(idioma, 'obras.tab.historial')}</h2>
      <Cronologia obra={obra} idioma={idioma} />
    </section>
  );
}
